import { createContext, useContext } from 'react';

/**
 * The rest of the app talks to authentication through this one interface, never
 * to `react-oidc-context` directly. That keeps the OIDC flow and the local
 * test-login flow interchangeable without conditional hooks at the call sites,
 * and means a future auth change touches only this folder.
 */
export type SessionUser = {
  displayName: string;
  subject: string;
};

export type Session = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user?: SessionUser;
  error?: string;
  /** Which provider is backing this session — surfaced in the UI so local setups are obvious. */
  mode: 'oidc' | 'test-login';
  signIn: () => void;
  signOut: () => void;
  /**
   * Drop the local session without a redirect — the user lands on the sign-in
   * gate. Used when the API definitively rejects the token (401 after a forced
   * refresh). `rejectedToken` is the token the API refused (undefined if the
   * request carried none); a provider holding a different token by then — the
   * user signed in while the request was in flight — keeps its session.
   * Optional: not every provider needs it.
   */
  clearSession?: (rejectedToken: string | undefined) => void;
  /**
   * Returns a bearer token, refreshing it if the provider supports that.
   * `forceRenew` skips the local expiry check — pass it when the server has
   * rejected the current token. Resolves `undefined` (never rejects) when no
   * usable token exists.
   */
  getAccessToken: (opts?: { forceRenew?: boolean }) => Promise<string | undefined>;
};

export const SessionContext = createContext<Session | undefined>(undefined);

export const useSession = (): Session => {
  const session = useContext(SessionContext);

  if (!session) {
    throw new Error('useSession must be used inside a SessionProvider');
  }

  return session;
};
